'use client'

/**
 * Ties PostHog's anonymous visitor to the signed-in account, and cuts the tie
 * on sign-out or when a page settles signed out over a persisted identified
 * id, so the next person on this browser is not attributed to the last.
 * Renders nothing; lives inside `AuthProvider` in the root layout.
 *
 * The distinct id is the auth user id — a stable identifier PostHog can merge
 * pre-login events into. Email is the one person property set, so a person in
 * PostHog is readable without a lookup; no user record content and no org
 * data travels with it. The local mock sign-in's stand-in account
 * (lib/auth/mock-auth.ts) is never identified: it is not a person, and every
 * developer running the harness would otherwise merge into one shared person.
 * Nothing runs where the SDK never started
 * (`instrumentation-client.ts` decides that), so a customer surface or a
 * token URL never identifies anyone. Every identify is announced
 * (`notifyPostHogIdentified`), because `reset()` clears the org attribution
 * `PostHogOrganization` registered and a recovered session must get it back.
 */

import { useEffect, useRef } from 'react'
import { useAuth } from '@/components/auth-provider'
import { notifyPostHogIdentified, posthogClient } from '@/lib/analytics/posthog-client'
import { MOCK_AUTH_USER } from '@/lib/auth/mock-auth'

export default function PostHogIdentity() {
  const { user, loading } = useAuth()
  const identifiedRef = useRef<string | null>(null)

  useEffect(() => {
    if (loading) return
    let cancelled = false
    void posthogClient().then((posthog) => {
      if (!posthog || cancelled) return
      if (user && user.id !== MOCK_AUTH_USER.id) {
        if (identifiedRef.current !== user.id) {
          posthog.identify(user.id, { email: user.email })
          identifiedRef.current = user.id
          // A reset a beat ago took the org super properties and $groups with
          // it; PostHogOrganization listens for this and puts them back.
          notifyPostHogIdentified()
        }
        return
      }
      if (identifiedRef.current || posthog.get_property('$user_state') === 'identified') {
        posthog.reset()
        identifiedRef.current = null
      }
    })
    return () => {
      cancelled = true
    }
  }, [user, loading])

  return null
}
